import './zone-flags'; // Must precede zone.js — unpatches `wheel` so mapbox preventDefault works (see file).
import 'zone.js';

// V8 caps error stacks at 10 frames by default — Angular's injector
// recursion alone is deeper than that, so DI errors (NG0201 etc.) reach
// PostHog with framework-only frames and the failing factory cut off.
// Raise the cap before anything can throw.
// (V8-only property — absent from TS's ErrorConstructor lib type.)
(Error as ErrorConstructor & { stackTraceLimit?: number }).stackTraceLimit = 64;
import { bootstrapApplication } from '@angular/platform-browser';
import { appConfig } from './app/app.config';
import { App } from './app/app';
import { isTauri, isMacOS, routeWindowOpenToSystemBrowser } from './app/core/tauri-runtime';
import { isFlowWidgetSurface } from './app/core/desktop-flow';
import { installDesktopMapCache } from './app/core/desktop-map-cache';
import { ensureMapboxCss } from './app/core/utils/mapbox-css';
import { registerAppGlyphs } from './app/core/constants/app-glyphs';
import { normaliseSignageHandoffLocation } from './app/features/signage/kernel/signage-handoff';
import { rememberTestHarnessSession } from './app/features/shell/preview-harness';
import { normaliseJoinPathLocation } from './app/core/utils/flow-join-surface';

// Tag the document for desktop-only CSS (drag region, traffic-light padding).
// Set before bootstrap so first paint already has the right chrome.
if (isTauri) {
  document.documentElement.classList.add('is-tauri');
  if (isMacOS) document.documentElement.classList.add('is-tauri-mac');
  // The Flow widget window: a transparent page over macOS's own frosted material.
  if (isFlowWidgetSurface()) document.documentElement.classList.add('is-flow-widget');
  // Map bundles from the Rust disk cache — before bootstrap so the first
  // floor the map engine fetches already goes through it.
  installDesktopMapCache();
  // `window.open(<external address>)` goes to the system browser: the webview
  // refuses new windows, so without this a link a service opens does nothing.
  routeWindowOpenToSystemBrowser();
}

// The glyphs Paper does not ship (today: the Hubs workspace mark). MUST run
// before bootstrap: `paper-icon` resolves a name against `ICON_REGISTRY` when
// it renders and never re-resolves, so a name registered after the shell bar
// has drawn its first tile stays an empty box for the life of the page.
registerAppGlyphs();

// Before the router's first navigation drops the query string (dev only).
rememberTestHarnessSession();

// A shared hosted-meeting link arrives as a PATH (`/m/<code>`) but the app is
// hash-routed: nginx 302s it to `/#/m/<code>`; the dev server and the desktop
// app have no nginx, so rewrite it here, before the router reads the URL.
normaliseJoinPathLocation();

if (!normaliseSignageHandoffLocation()) {
  bootstrapApplication(App, appConfig)
    .catch((err) => console.error(err));
}

// Warm the (no-longer-render-blocking) Mapbox stylesheet once the page has
// painted, so any map surface reached later already has it. Map-creating
// code also calls ensureMapboxCss() directly for ordering guarantees.
const warmMapboxCss = (): void => {
  const idle = (window as Window & { requestIdleCallback?: (cb: () => void) => void })
    .requestIdleCallback ?? ((cb: () => void) => setTimeout(cb, 1500));
  idle(() => void ensureMapboxCss());
};
if (document.readyState === 'complete') warmMapboxCss();
else window.addEventListener('load', warmMapboxCss, { once: true });
